import { closestCenter, DndContext, type DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { ReactNode } from 'react';

import type { WidgetConfig } from '@/types/dashboards';

import SortableWidgetCard from './SortableWidgetCard';

interface WidgetCanvasProps {
    widgets: WidgetConfig[];
    editable?: boolean;
    onReorder?: (widgets: WidgetConfig[]) => void;
    onEdit?: (widget: WidgetConfig) => void;
    onRemove?: (id: string) => void;
    /** Renders the chart body for a widget — live preview in the builder, precomputed result in a saved view. */
    renderBody: (widget: WidgetConfig) => ReactNode;
    /** Renders the widget's "(i)" data-point explanation, when the result carries one. */
    renderInfo?: (widget: WidgetConfig) => ReactNode;
}

/**
 * Auto-flow responsive grid of widget cards with drag-to-reorder (dnd-kit,
 * mirroring StatusEditor). The DnD context is always mounted so useSortable has
 * its provider; dragging is simply disabled per-card when not editable.
 */
export default function WidgetCanvas({ widgets, editable = false, onReorder, onEdit, onRemove, renderBody, renderInfo }: WidgetCanvasProps) {
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = widgets.findIndex((w) => w.id === active.id);
        const newIndex = widgets.findIndex((w) => w.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
            onReorder?.(arrayMove(widgets, oldIndex, newIndex));
        }
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={widgets.map((w) => w.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
                    {widgets.map((widget) => (
                        <SortableWidgetCard
                            key={widget.id}
                            widget={widget}
                            editable={editable}
                            onEdit={onEdit}
                            onRemove={onRemove}
                            info={renderInfo?.(widget)}
                        >
                            {renderBody(widget)}
                        </SortableWidgetCard>
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}
